import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  BuildingStorefrontIcon,
  RocketLaunchIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { ClipLoader } from 'react-spinners';

import ScaleBox from 'components/ScaleBox';
import { EasypayLockedHint } from 'components/EasypayPayout';
import CategoryOverflow from 'components/CategoryOverflow';
import normalizeEventDate from 'utils/normalizeEventDate';
import { computeBookingPricingFromTotal } from 'utils/bookingPricing';
import { isEventCancelled } from 'utils/eventStatus';

const CARD_BASE_WIDTH = 560;
const CARD_BASE_HEIGHT = 390;

const getEventWindow = (dateObj) => {
  if (!dateObj || !dateObj.type) return { start: null, end: null };

  if (dateObj.type === 'fixed') {
    const start = moment(
      `${dateObj.startDate} ${dateObj.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${dateObj.endDate} ${dateObj.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const sorted = [...dateObj.days]
      .filter((d) => d?.date)
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    const start = moment(
      `${first.date} ${first.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${last.date} ${last.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return { start, end };
  }

  return { start: null, end: null };
};

const formatEventDateRange = (date, locale) => {
  if (!date || !date.type) return '';

  if (date.type === 'fixed') {
    const start = moment(date.startDate);
    const end = moment(date.endDate);
    if (!start.isValid() || !end.isValid()) return '';

    const sameMonthYear =
      start.year() === end.year() && start.month() === end.month();
    const sameYear = start.year() === end.year();

    if (sameMonthYear) {
      return `${start.locale(locale).format('D')}–${end.locale(locale).format('D MMM YYYY')}`;
    }
    if (sameYear) {
      return `${start.locale(locale).format('D MMM')} – ${end.locale(locale).format('D MMM YYYY')}`;
    }
    return `${start.locale(locale).format('D MMM YYYY')} – ${end.locale(locale).format('D MMM YYYY')}`;
  }

  if (
    date.type === 'segmented' &&
    Array.isArray(date.days) &&
    date.days.length
  ) {
    const sorted = [...date.days]
      .filter((d) => moment(d?.date).isValid())
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    if (!sorted.length) return '';

    const first = moment(sorted[0].date);
    const last = moment(sorted[sorted.length - 1].date);
    const sameMonthYear =
      first.year() === last.year() && first.month() === last.month();
    const sameYear = first.year() === last.year();

    if (sameMonthYear) {
      return `${first.locale(locale).format('D')}–${last.locale(locale).format('D MMM YYYY')}`;
    }
    if (sameYear) {
      return `${first.locale(locale).format('D MMM')} – ${last.locale(locale).format('D MMM YYYY')}`;
    }
    return `${first.locale(locale).format('D MMM YYYY')} – ${last.locale(locale).format('D MMM YYYY')}`;
  }

  return '';
};

const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : v || '');
const getCardLocationLabel = (ev) => {
  const city = trimOrEmpty(ev?.city);
  const country = trimOrEmpty(ev?.country) || trimOrEmpty(ev?.countryCode);
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return trimOrEmpty(ev?.location);
};

// Cents only when there are cents. Both bounds must move together: Intl
// throws a RangeError when the minimum exceeds the maximum, and a thrown
// render here blanks the whole dashboard. Rounding first drops the float
// dust a sum of net prices picks up, so 1200.0000000000002 prints as
// €1,200 and not €1,200.00. Mirrors formatEuro in EventManagerModal.
const formatEuro = (n) => {
  const num = Math.round((Number(n) || 0) * 100) / 100;
  const fractionDigits = num % 1 === 0 ? 0 : 2;
  return `€${num.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

// What the promoter earns: the stand price, net of the There2B fee and VAT
// the brand paid on top. Prefer the price snapshot on the booking's own
// Space (Sequelize serialises the association capitalised); fall back to
// unwinding the stored total, which is what the brand was actually charged
// and survives a later price edit on the space.
// Kept in sync with SpacesOverviewPanel, the other promoter-facing revenue.
const bookingNetPrice = (b) => {
  const price = Number(b?.Space?.price ?? b?.space?.price);
  if (Number.isFinite(price)) return price;
  if (b?.totalAmount == null) return null;
  const { price: derived } = computeBookingPricingFromTotal(b.totalAmount, {
    at: b?.createdAt,
  });
  return Number.isFinite(derived) ? derived : null;
};

const computeRevenue = (bookings = []) =>
  bookings.reduce((sum, b) => {
    if (b?.status !== 'Paid') return sum;
    const price = bookingNetPrice(b);
    return price === null ? sum : sum + price;
  }, 0);

// Pending is the effective status the API sends, so applications whose
// window has already lapsed arrive as Expired and are not counted.
const countRequests = (bookings = []) =>
  bookings.filter((b) => b?.status === 'Pending').length;

const getCapacityState = (booked, total, t) => {
  if (!total) {
    return {
      label: t('dashboard.card.empty'),
      barColor: 'bg-emerald-500',
      textColor: 'text-emerald-600',
      pct: 0,
    };
  }
  const pct = Math.min((booked / total) * 100, 100);
  if (pct >= 100)
    return {
      label: t('dashboard.card.sold_out'),
      barColor: 'bg-pink-500',
      textColor: 'text-pink-600',
      pct,
    };
  if (pct >= 67)
    return {
      label: t('dashboard.card.almost_full'),
      barColor: 'bg-orange-400',
      textColor: 'text-orange-500',
      pct,
    };
  if (pct >= 34)
    return {
      label: t('dashboard.card.half_full'),
      barColor: 'bg-yellow-400',
      textColor: 'text-yellow-500',
      pct,
    };
  if (pct > 0)
    return {
      label: t('dashboard.card.almost_empty'),
      barColor: 'bg-emerald-400',
      textColor: 'text-emerald-600',
      pct,
    };
  return {
    label: t('dashboard.card.empty'),
    barColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    pct,
  };
};

const MyEventCard = ({
  event,
  onManageClick,
  onDuplicateClick,
  onPublishClick,
  onShareClick,
  publishLocked = false,
  publishing = false,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  if (!event) return null;

  const normalizedDate = normalizeEventDate(event.date);
  const { start, end } = getEventWindow(normalizedDate || {});
  const now = moment();

  const isDraft = String(event.status || '').toLowerCase() === 'draft';
  const isLive = start && end && now.isSameOrAfter(start) && now.isBefore(end);
  const isPast = end && now.isSameOrAfter(end);
  // Worth sharing while brands can still see and book it.
  const canShare = Boolean(onShareClick) && !isDraft && !isPast && !isEventCancelled(event);
  const daysUntil =
    start && now.isBefore(start) ? Math.max(start.diff(now, 'days'), 0) : null;

  const total = Number(event.totalSpaces) || 0;
  const booked = Number(event.bookedSpaces) || 0;
  const bookings = Array.isArray(event.bookings) ? event.bookings : [];
  const revenue = computeRevenue(bookings);
  const requests = countRequests(bookings);

  const capacity = getCapacityState(booked, total, t);
  const dateRange = formatEventDateRange(
    normalizedDate,
    i18n.language?.startsWith('pt') ? 'pt' : 'en'
  );
  const locationLabel = getCardLocationLabel(event);

  const handleNavigateToEventDetail = () => {
    if (event.slug) navigate(`/event/${event.slug}`);
  };

  const renderStatusBadge = () => {
    // A draft's dates say nothing about its state — nobody else can see it.
    if (isDraft) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 text-gray-700 text-[11px] font-bold tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
          {t('dashboard.card.draft')}
        </span>
      );
    }
    if (isPast) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-gray-600 text-[11px] font-bold tracking-wide">
          {t('dashboard.card.ended')}
        </span>
      );
    }
    if (isLive) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 text-emerald-600 text-[11px] font-bold tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {t('dashboard.card.live_now')}
        </span>
      );
    }
    if (Number.isFinite(daysUntil)) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 text-gray-700 text-[11px] font-bold tracking-wide">
          {t('dashboard.card.in_days', { count: daysUntil })}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="relative w-full">
      <ScaleBox
        baseWidth={CARD_BASE_WIDTH}
        baseHeight={CARD_BASE_HEIGHT}
        ratioClass="aspect-[560/390]"
      >
        <div className="h-full w-full p-1">
          <div
            className={`h-full w-full bg-white rounded-[24px] shadow-[0_1px_8px_rgba(15,23,42,0.12)] hover:shadow-[0_4px_14px_rgba(15,23,42,0.14)] transition-shadow overflow-hidden flex flex-col ${
              isPast && !isDraft ? 'grayscale opacity-75' : ''
            }`}
          >
            {/* Hero image */}
            <div className="relative h-[165px] flex-shrink-0">
              {event.avatar ? (
                <img
                  src={event.avatar}
                  alt={event.name || 'Event'}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="absolute inset-0 bg-gray-200 grid place-items-center">
                  <BuildingStorefrontIcon className="w-16 h-16 text-gray-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/5" />

              <div className="absolute top-4 left-4 z-10">
                {renderStatusBadge()}
              </div>

              <div className="absolute bottom-4 left-5 right-5 text-white">
                {(locationLabel || dateRange) && (
                  <p className="text-sm font-bold opacity-90 drop-shadow truncate">
                    {[locationLabel, dateRange].filter(Boolean).join(' · ')}
                  </p>
                )}
                <h3
                  title={event.name}
                  className="font-extrabold text-[21px] leading-[1.08] drop-shadow line-clamp-1"
                >
                  {event.name}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 flex flex-col px-5 pt-5 pb-4 min-h-0">
              {isDraft ? (
                // Bookings, revenue and capacity are all zero on a draft; what
                // the promoter needs here is the way to publish it.
                <div className="flex-1 min-h-0 flex flex-col justify-center rounded-2xl bg-gray-50 border border-dashed border-gray-300 px-5 py-4">
                  <p className="text-[15px] font-extrabold text-gray-900">
                    {t('dashboard.card.draft_title')}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-gray-500">
                    {publishLocked
                      ? t('dashboard.card.draft_body_locked')
                      : t('dashboard.card.draft_body')}
                  </p>
                  {onPublishClick && (
                    <div className="mt-3 flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onPublishClick(event)}
                        disabled={publishLocked || publishing}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-extrabold text-gray-900 bg-yellow-400 hover:bg-yellow-500 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow-400"
                      >
                        {publishing ? (
                          <ClipLoader color="#111827" size={14} />
                        ) : (
                          <RocketLaunchIcon className="w-4 h-4" />
                        )}
                        {t('dashboard.card.publish')}
                      </button>
                      {publishLocked && <EasypayLockedHint align="center" />}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2.5 flex-shrink-0">
                    <div className="h-[66px] rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                      <p className="text-[11px] leading-none font-extrabold tracking-[0.16em] text-gray-500">
                        {t('dashboard.card.booked')}
                      </p>
                      <p className="mt-2 text-[22px] font-extrabold text-gray-900 leading-none">
                        {booked}
                        <span className="text-gray-400 font-bold text-lg">
                          /{total}
                        </span>
                      </p>
                    </div>
                    <div className="h-[66px] rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                      <p className="text-[11px] leading-none font-extrabold tracking-[0.16em] text-gray-500">
                        {t('dashboard.card.revenue')}
                      </p>
                      <p className="mt-2 text-[22px] font-extrabold text-gray-900 leading-none">
                        {formatEuro(revenue)}
                      </p>
                    </div>
                    <div
                      className={`h-[66px] rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
                        requests > 0
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-gray-50 border border-gray-100'
                      }`}
                    >
                      <p
                        className={`text-[11px] leading-none font-extrabold tracking-[0.16em] ${
                          requests > 0 ? 'text-yellow-700' : 'text-gray-500'
                        }`}
                      >
                        {t('dashboard.card.requests')}
                      </p>
                      <p
                        className={`mt-2 text-[22px] font-extrabold leading-none ${
                          requests > 0 ? 'text-yellow-800' : 'text-gray-900'
                        }`}
                      >
                        {requests}
                      </p>
                    </div>
                  </div>

                  {/* Progress (vertically centered between stats and footer) */}
                  <div className="flex-1 min-h-[76px] flex flex-col justify-center">
                    <div className="flex items-center justify-between text-sm font-extrabold tracking-[0.12em] mb-2">
                      <span className={capacity.textColor}>
                        {capacity.label}
                      </span>
                      <span className="text-gray-500 tracking-normal">
                        {booked}/{total}
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                      <div
                        className={`h-full ${capacity.barColor} transition-all duration-300`}
                        style={{ width: `${capacity.pct}%` }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Footer */}
              <div className="mt-auto flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex-1 min-w-0 flex items-center overflow-visible">
                  <CategoryOverflow
                    categories={event.categoriesAllowed}
                    maxVisible={7}
                    macroIconClass="w-6 h-6"
                    align="start"
                    visibleContainerClass="flex items-center gap-1.5 flex-nowrap overflow-visible [&>div]:grid [&>div]:h-10 [&>div]:w-10 [&>div]:place-items-center [&>div]:rounded-xl [&>div]:bg-gray-50 [&>div]:shadow-sm"
                    panelContainerClass="flex items-center gap-2 flex-wrap overflow-visible [&>div]:grid [&>div]:h-10 [&>div]:w-10 [&>div]:place-items-center [&>div]:rounded-xl [&>div]:bg-gray-50 [&>div]:shadow-sm"
                    buttonClass="shrink-0 inline-flex items-center justify-center h-10 px-2.5 rounded-xl bg-gray-50 text-gray-600 text-sm font-bold shadow-sm hover:bg-gray-100 transition"
                  />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {canShare && (
                    <button
                      type="button"
                      onClick={() => onShareClick(event)}
                      aria-label={t('share_kit.share_button')}
                      title={t('share_kit.share_button')}
                      className="grid place-items-center w-[38px] h-[38px] rounded-full text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
                    >
                      <ShareIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleNavigateToEventDetail}
                    className="whitespace-nowrap px-5 py-2 rounded-full text-sm font-bold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
                  >
                    {t('common.view')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onManageClick && onManageClick(event)}
                    className="whitespace-nowrap px-5 py-2 rounded-full text-sm font-extrabold text-gray-900 bg-yellow-400 hover:bg-yellow-500 shadow-sm"
                  >
                    {t('common.manage')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScaleBox>
    </div>
  );
};

export default MyEventCard;
